import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flex, Pagination, Spin, notification } from "antd";

import { ReactComponent as DotsActionsIcon } from "../../../../../../assets/icons/ThreeDotsVertical.svg";

import { fetchWorkoutsStart } from "../../../../../../store/features/workouts/workouts-slice";
import { useAppDispatch, useAppSelector } from "../../../../../../store/config";
import {
  IMAGE_BASE_URL,
  CARDS_PAGE_SIZE,
} from "../../../../../../common/config";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import {
  SortType,
  PaginationType,
} from "../../../../../../interfaces/screens/fetch-params-types";

import {
  NotFoundText,
  StatusTag,
} from "../../../../../../components/common/styles";
import { NewWorkoutModal } from "./NewWorkoutModal";
import DeleteModal from "../../../../../../components/common/DeleteModal";
import {
  deleteWorkoutStart,
  updateWorkoutStart,
} from "../../../../../../store/features/workout/workout-slice";
import {
  StyledCardTableLayout,
  StyledCardMetaWrapper,
  StyledCard,
  StyledCardImage,
  StyledCardMeta,
  CardActionsButton,
  StyledCardMetaTitle,
} from "../../../../../../components/common/styles/tableStyles";
import { TableHeader } from "../../../../../../components/table/TableHeader";
import { IFetchParams } from "../../../../../../interfaces/base";

export const WorkoutsTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();
  const { id: trainerId } = useParams<{ id: string }>();

  const [newWorkoutmodalOpen, setNewWorkoutModalOpen] =
    useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [currentWorkoutId, setCurrentWorkoutId] = useState<string>("");
  const [deleteConfirmLoading, setDeleteConfirmLoading] =
    useState<boolean>(false);

  const { workouts, loading, error, metadata, tableState } = useAppSelector(
    (state) => state.workouts
  );
  const { search, sort, pagination } = tableState;
  const [inputSearch, setInputSearch] = useState<string>(search);

  const debouncedSearch = useDebounce(inputSearch, 300);

  const {
    error: deleteError,
    operation: deleteOperation,
    requestState: deleteRequestState,
  } = useAppSelector((state) => state.workout);

  useEffect(() => {
    const paginationToUse =
      debouncedSearch !== search
        ? { page: 0, limit: CARDS_PAGE_SIZE, skip: 0 }
        : pagination;

    fetchWorkouts({ pagination: paginationToUse });
  }, [debouncedSearch]);

  useEffect(() => {
    if (error || deleteError) {
      openNotification();
    }
  }, [error || deleteError]);

  useEffect(() => {
    if (!openDeleteModal) {
      setCurrentWorkoutId("");
    }
  }, [openDeleteModal]);

  useEffect(() => {
    if (
      deleteRequestState === "finish" &&
      (deleteOperation === "delete" || deleteOperation === "update")
    ) {
      setDeleteConfirmLoading(false);
      setOpenDeleteModal(false);
      fetchWorkouts({});
    }
  }, [deleteRequestState, deleteOperation]);

  const openNotification = () => {
    api["error"]({
      message: t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const onPaginationChange = (page: number) => {
    const newPagination = {
      ...pagination,
      page,
      skip: CARDS_PAGE_SIZE * (page - 1),
    };

    fetchWorkouts({ pagination: newPagination });
  };

  const handleSetSort = (value: SortType) => {
    fetchWorkouts({
      sort: value,
      pagination: { page: 0, limit: CARDS_PAGE_SIZE, skip: 0 },
    });
  };

  const handleDelete = () => {
    setDeleteConfirmLoading(true);
    dispatch(deleteWorkoutStart({ workoutId: currentWorkoutId }));
  };

  const handleCreateNewWorkout = () => {
    setNewWorkoutModalOpen(true);
  };

  const handleToggleEnableWorkout = (workoutId: string, enabled: boolean) => {
    setCurrentWorkoutId(workoutId);

    dispatch(
      updateWorkoutStart({
        workoutId,
        workout: { enabled },
      })
    );
  };

  const fetchWorkouts = (body: IFetchParams) => {
    dispatch(
      fetchWorkoutsStart({
        search: body.search || debouncedSearch,
        sort: body.sort || sort,
        pagination: body.pagination || pagination,
        filter: { trainer: trainerId, trainingProgram: null },
      })
    );
  };

  return (
    <>
      {contextHolder}
      <TableHeader
        search={inputSearch}
        setSearch={setInputSearch}
        setSort={handleSetSort}
        handleCreateNew={handleCreateNewWorkout}
        createNewText="Create New Workout"
        currentSortBy={sort?.sortBy || ""}
      />
      <NewWorkoutModal
        trainerId={trainerId}
        modalOpen={newWorkoutmodalOpen}
        setModalOpen={setNewWorkoutModalOpen}
        navigateTo={`/trainers/${trainerId}/workouts/add`}
      />
      <Spin spinning={loading}>
        {workouts.length === 0 && !loading && (
          <NotFoundText>{t("No workouts found")}</NotFoundText>
        )}
        <StyledCardTableLayout>
          {workouts.map((workout) => (
            <StyledCard
              key={workout._id}
              cover={
                <StyledCardImage
                  src={IMAGE_BASE_URL + "/" + workout.outerCover}
                />
              }
            >
              <CardActionsButton
                menu={{
                  items: [
                    {
                      key: "edit",
                      label: t("Edit"),
                      onClick: () =>
                        navigate(
                          `/trainers/${trainerId}/workouts/${workout._id}`,
                          { state: { isEdit: true } }
                        ),
                    },
                    {
                      key: "updateStatus",
                      label:
                        t("Change Status To-") +
                        " " +
                        (workout.enabled ? t("Inactive") : t("Active")),
                      onClick: () => {
                        handleToggleEnableWorkout(
                          workout._id,
                          !workout.enabled
                        );
                      },
                    },
                    {
                      key: "delete",
                      label: t("Delete"),
                      onClick: () => {
                        setCurrentWorkoutId(workout._id);
                        setOpenDeleteModal(true);
                      },
                    },
                  ],
                }}
              >
                <DotsActionsIcon />
              </CardActionsButton>
              <StyledCardMetaWrapper
                onClick={() =>
                  navigate(`/trainers/${trainerId}/workouts/${workout._id}`)
                }
              >
                <StyledCardMeta
                  title={
                    <Flex justify="space-between" align="center">
                      <StyledCardMetaTitle>{workout.name}</StyledCardMetaTitle>
                      <StatusTag active={!!workout.enabled}>
                        {t(!!workout.enabled ? "Active" : "Inactive")}
                      </StatusTag>
                    </Flex>
                  }
                  description={workout.description || ""}
                />
              </StyledCardMetaWrapper>
            </StyledCard>
          ))}
        </StyledCardTableLayout>
        <Pagination
          current={metadata?.page}
          pageSize={CARDS_PAGE_SIZE}
          total={metadata?.total}
          align="center"
          hideOnSinglePage
          onChange={onPaginationChange}
          showSizeChanger={false}
          style={{ direction: "ltr", marginTop: "60px" }}
        />
        <DeleteModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          handleOk={handleDelete}
          confirmLoading={deleteConfirmLoading}
        />
      </Spin>
    </>
  );
};
